import { motion } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { CHAPTERS, METRICS, IMAGES } from "@/lib/site";

function InnerHero() {
  return (
    <section className="relative overflow-hidden pb-8 pt-40" data-testid="about-hero">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(0,122,255,0.16),transparent_50%)]" />
      <div className="relative z-10 mx-auto max-w-6xl px-6">
        <Reveal>
          <Overline>About StratFlow Tech</Overline>
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            <span className="text-slate-900">We are an engineering studio </span>
            <span className="metallic-text">obsessed with the details.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Headquartered in the HDS Towers of Jumeirah Lakes Towers, Dubai,
            StratFlow Tech FZCO is a multidisciplinary team building software
            that organisations depend on every single day.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Chapters() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-24" data-testid="manifesto-chapters">
      <div className="space-y-4">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.num} delay={i * 0.05}>
            <div className="group grid items-start gap-6 border-t border-slate-900/10 py-10 md:grid-cols-[180px_1fr] md:gap-12">
              <div className="font-mono-x text-6xl font-medium text-slate-900/10 transition-colors duration-500 group-hover:text-blue-500/70 md:text-7xl">
                {c.num}
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {c.title}
                </h3>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                  {c.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ImageBreak() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-10" data-testid="about-image">
      <Reveal className="relative overflow-hidden rounded-[2rem] border border-slate-900/10">
        <motion.img
          src={IMAGES.workspace}
          alt="Modern engineering workspace"
          className="h-[280px] w-full img-tech object-cover md:h-[440px]"
          initial={{ scale: 1.12 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: "easeOut" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#e9eef5] via-transparent to-transparent" />
        <div className="absolute bottom-8 left-8 right-8">
          <p className="font-display text-2xl font-bold text-slate-900 md:text-3xl">
            A culture of craft, curiosity and calm under pressure.
          </p>
        </div>
      </Reveal>
    </section>
  );
}

function Stats() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16" data-testid="about-stats">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-slate-900/10 bg-slate-900/[0.04] md:grid-cols-4">
        {METRICS.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.08} className="bg-[#eef2f8] p-8">
            <div className="metallic-text font-display text-4xl font-extrabold tracking-tighter md:text-5xl">
              {m.value}
            </div>
            <div className="mt-3 text-sm font-semibold text-slate-900">{m.label}</div>
            <div className="mt-1 text-xs text-slate-500">{m.sub}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section className="mx-auto max-w-6xl px-6 py-14" data-testid="about-gallery">
      <Reveal>
        <Overline>Inside the studio</Overline>
        <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Where the work actually happens.
        </h2>
      </Reveal>
      <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
        {IMAGES.culture.map((src, i) => (
          <Reveal
            key={src}
            delay={i * 0.08}
            className={`overflow-hidden rounded-2xl border border-slate-900/10 ${
              i === 0 ? "col-span-2 row-span-2 md:col-span-2 md:row-span-2" : ""
            }`}
          >
            <div className="group relative h-full w-full">
              <img
                src={src}
                alt="StratFlow studio"
                loading="lazy"
                className={`w-full img-tech object-cover opacity-80 grayscale-[30%] transition-all duration-700 group-hover:scale-105 group-hover:opacity-100 group-hover:grayscale-0 ${
                  i === 0 ? "h-64 md:h-full" : "h-32 md:h-40"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#e9eef5]/60 to-transparent" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <InnerHero />
      <Chapters />
      <ImageBreak />
      <Gallery />
      <Stats />
      <div className="py-10">
        <Marquee />
      </div>
    </div>
  );
}
